import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { lineColor, type ChartVariant } from "../charts/WagmiLineChart";
import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";

// PG-26…PG-29 — la riga sotto l'headline di una riga e1RM: il nome
// dell'esercizio CONFRONTATO e i due controlli che cambiano l'accoppiata.
// Split da TierOneTrendRow alla nascita: quel file era a 405 su 500.
//
// ⭐ Il disegno è suo, e la seconda stesura (17/09 notte) è la sua correzione
// della prima, che aveva capito male:
//
//     Back Squat vs          ← l'headline, colorato come la curva corrente
//     Hack Squat Machine ⇄   ← QUESTA riga, colorata come la curva precedente
//
// ⛔ Il nome corrente NON si ripete qui: sta già nell'headline, e il `vs` gli
// si appende là. La prima stesura ne disegnava una copia su questa riga e lui
// l'ha vista subito. [[feedback_never_add_or_remove_what_he_did_not_ask]]
//
// ⛔ NIENTE swatch/trattini davanti ai nomi. Erano una mia proposta, l'aveva
// accettata a parole e poi rifiutata a vista: *«senza mettere le linee nel
// testo»*. Il colore dei due nomi È la legenda, da solo.
// ⚠️ Il prezzo, dichiarato e suo da decidere: la legenda poggia ora sul SOLO
// colore, che è la deroga che `TierOneTrendRow.tsx` dichiara nel tipo. Chi non
// distingue i due toni perde l'accoppiata. ⭐ Non vale per chi usa uno screen
// reader: `a11yLabel` dice a parole quale curva è quale e di chi è la scelta.
export type TierOneCompareRowProps = {
  theme: UiTheme;
  /**
   * Quale variante di grafico è la curva confrontata. ⛔ Passata, mai
   * indovinata qui: la decide il chiamante, ed è `lineColor` a dare l'unico
   * colore con cui la linea è davvero disegnata.
   *
   * 🪤 `"focal"` è ESCLUSO dal tipo, e non è pedanteria: `lineColor` gli
   * restituisce `brand500` grezzo, che come TESTO è **3.12:1** su bianco —
   * sotto il pavimento di 4.5:1. La curva può usarlo (soglia 3:1 per il
   * non-testo), un nome no. L'headline infatti dipinge il gemello leggibile
   * `brandText`. Senza questo `Exclude` niente impedirebbe a un chiamante
   * futuro di far tornare qui il colore che non passa.
   */
  comparedVariant: Exclude<ChartVariant, "focal">;
  /** L'esercizio disegnato dietro. Assente = niente da confrontare. */
  compare?: {
    /** Solo il nome. ⛔ Il separatore vive sull'headline, non qui. */
    name: string;
    /**
     * Cosa dice l'intera accoppiata a uno screen reader. ⭐ È qui che
     * sopravvive la distinzione che lui ha tolto dallo SCHERMO: con un
     * separatore solo, chi vede distingue «scelto da me» da «calcolato»
     * unicamente dalla ✕, e chi non vede non la troverebbe.
     */
    a11yLabel: string;
  };
  /** Apre il picker. Resta dopo una scelta: serve a ri-cambiare, non solo ad
   *  aprire (sue parole su PG-27). Assente = nessun picker montato. */
  swap?: { a11yLabel: string; onPress: () => void };
  /** PG-28 — toglie la sua scelta e rimette il passaggio di consegne
   *  calcolato. Assente quando l'accoppiata è già quella calcolata. */
  clear?: { a11yLabel: string; onPress: () => void };
};

export function TierOneCompareRow({
  theme,
  comparedVariant,
  compare,
  swap,
  clear,
}: TierOneCompareRowProps) {
  const styles = useMemo(() => makeStyles(theme), [theme]);
  if (!compare && !swap) return null;
  return (
    // ⛔ NESSUN wrapper `accessible` qui: fonde il sottoalbero in UN nodo e i
    // bottoni smettono di essere raggiungibili come bottoni. Due reviewer
    // hanno preso quella regressione in modo indipendente, una volta.
    <View style={styles.row}>
      {compare ? (
        <Text
          style={[styles.compared, { color: lineColor(theme, comparedVariant) }]}
          numberOfLines={1}
          accessibilityLabel={compare.a11yLabel}
        >
          {compare.name}
        </Text>
      ) : null}
      {swap ? (
        <IconButton
          theme={theme}
          name="arrow-left-right"
          accessibilityLabel={swap.a11yLabel}
          // 🪤 Asimmetrico apposta. Il gap di questa riga è `s2` (4pt) e
          // l'hitSlop di default di IconButton è 8 per lato: una coppia ⇄/✕
          // che li ereditasse avrebbe le zone di tocco SOVRAPPOSTE di ~12pt —
          // la classe di mis-tap che IconButton ha già riparato una volta.
          // ⛔ Sul web non si vede: RNW droppa hitSlop e il webTapFloor a 44
          // copre il difetto. Solo un telefono può dirlo.
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 2 }}
          onPress={swap.onPress}
        />
      ) : null}
      {clear ? (
        <IconButton
          theme={theme}
          name="close"
          variant="cancel"
          accessibilityLabel={clear.a11yLabel}
          style={styles.clear}
          hitSlop={{ top: 8, bottom: 8, left: 2, right: 8 }}
          onPress={clear.onPress}
        />
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    row: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    // `flexShrink` e non `flex`: il nome cede spazio ai due bottoni invece di
    // spingerli fuori riga, e con `numberOfLines={1}` tronca da sé.
    compared: { fontSize: ty.sizes.sm, fontWeight: "600", flexShrink: 1 },
    // Lo spazio in più prima della ✕ distruttiva, oltre all'hitSlop tagliato.
    clear: { marginLeft: s.s2 },
  });
}
